import { useContext } from "react";
import {
  AddressBookDeleteContext,
  AddressBookHandlerContext,
  AddressBookIndexContext,
  AddressBookDeleteContextValue,
  AddressBookHandlerContextValue,
  AddressBookIndexContextValue,
} from "./index";

/* ===========================================================================
 * HOOK: useAddressBookIndexContext
 * =========================================================================== */

/**
 * @function useAddressBookIndexContext
 * Retrieves the value of the AddressBookIndexContext.
 * * Responsibilities:
 * - Accessing shared configuration and UI state for the AddressBook index.
 * - Ensuring the hook is used within the correct Provider boundary.
 *
 * @returns {AddressBookIndexContextValue} The current context value.
 * @throws {Error} If called outside of an AddressBookIndexProvider.
 */
export const useAddressBookIndexContext = (): AddressBookIndexContextValue => {
  const context = useContext(AddressBookIndexContext);
  if (!context) {
    throw new Error(
      "useAddressBookIndexContext must be used within an AddressBookIndexProvider",
    );
  }
  return context;
};

/* ===========================================================================
 * HOOK: useAddressBookHandlerContext
 * =========================================================================== */

/**
 * @function useAddressBookHandlerContext
 * Retrieves the value of the AddressBookHandlerContext.
 * * Responsibilities:
 * - Providing access to CRUD operations and form state management.
 * - Enforcing the presence of the AddressBookHandlerProvider in the tree.
 *
 * @returns {AddressBookHandlerContextValue} The current handler context value.
 * @throws {Error} If called outside of an AddressBookHandlerProvider.
 */
export const useAddressBookHandlerContext =
  (): AddressBookHandlerContextValue => {
    const context = useContext(AddressBookHandlerContext);
    if (!context) {
      throw new Error(
        "useAddressBookHandlerContext must be used within an AddressBookHandlerProvider",
      );
    }
    return context;
  };

/* ===========================================================================
 * HOOK: useAddressBookDeleteContext
 * =========================================================================== */

/**
 * @function useAddressBookDeleteContext
 * Retrieves the value of the AddressBookDeleteContext.
 * * Responsibilities:
 * - Exposing batch deletion logic and loading states for selected records.
 * - Validating the execution context within an AddressBookDeleteProvider.
 *
 * @returns {AddressBookDeleteContextValue} The current delete-context value.
 * @throws {Error} If called outside of an AddressBookDeleteProvider.
 */
export const useAddressBookDeleteContext =
  (): AddressBookDeleteContextValue => {
    const context = useContext(AddressBookDeleteContext);
    if (!context) {
      throw new Error(
        "useAddressBookDeleteContext must be used within an AddressBookDeleteProvider",
      );
    }
    return context;
  };
